import { Tooltip, TooltipProps } from '@components/common/Tooltip/Tooltip';
import { BothOrNeither } from '@utils';
import clsx from 'clsx';
import { ChangeEvent, ReactNode, useEffect, useRef } from 'react';
import { Label } from '../Typography/Label';
import { Paragraph } from '../Typography/Paragraph';

type BaseProps = {
  name: string;
  checked: boolean;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  label?: ReactNode;
  helpText?: string;
  /**
   * Indicates that the checkbox is in an indeterminate state.
   * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/checkbox#indeterminate_state_checkboxes
   */
  isIndeterminate?: boolean;
  error?: string;
};

type DisabledProps = BothOrNeither<
  { disabled: boolean },
  { disabledTooltip: TooltipProps }
>;

type Props = BaseProps & DisabledProps;

export const Checkbox = ({
  label,
  helpText,
  name,
  checked,
  onChange,
  disabled = false,
  disabledTooltip,
  isIndeterminate = false,
  error,
}: Props) => {
  const ref = useRef<HTMLInputElement>(null!);

  useEffect(() => {
    if (isIndeterminate) {
      ref.current.indeterminate = !checked && isIndeterminate;
    }
  }, [ref, checked, isIndeterminate]);

  const checkboxComponent = (
    <div className="flex items-center gap-2">
      <input
        ref={ref}
        id={name}
        name={name}
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        aria-checked={checked}
        className={clsx(
          'text-product-dark checked:bg-product-dark focus:ring-product-dark h-4 w-4 rounded border border-gray-300 text-2xl focus:ring-2 disabled:bg-gray-300',
          error && 'border-red-600',
        )}
      />
      {label &&
        (typeof label === 'string' ? (
          <Label
            htmlFor={name}
            className={clsx('cursor-pointer', disabled && 'text-gray-400!')}
          >
            {label}
          </Label>
        ) : (
          label
        ))}
    </div>
  );

  return (
    <div className="relative flex flex-col items-start gap-1">
      {disabled && disabledTooltip ? (
        <Tooltip {...disabledTooltip}>{checkboxComponent}</Tooltip>
      ) : (
        checkboxComponent
      )}
      {helpText && (
        <Paragraph
          color={clsx('text-gray-500', disabled && 'text-gray-400')}
          className="ml-6"
        >
          {helpText}
        </Paragraph>
      )}
      {error && <Paragraph color="text-red-600">{error}</Paragraph>}
    </div>
  );
};
